import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import pluralize from "pluralize";
import React from "react";

import ImageQueryThumbnail from "components/common/ImageQueryThumbnail";
import { labelInternalToUI, labelMappingsIQ, sourceMappingsIQ } from "config/LabelMapping";
import { Typography } from "new-components/typography/Typography";
import { DetectorModes } from "types/createDetector";
import { Detector } from "types/detectors";
import { ImageQuery } from "types/imageQueries";
import { convertUTCtoLocalReadableDate } from "utils/utilityFunctions";

import { Header } from "./header/Header";

const HIDEABLE_COLUMNS_BY_MODE = {
  [DetectorModes.BINARY]: [],
  [DetectorModes.COUNT]: [
    "system_answer_confidence",
    "system_prediction_confidence",
    "ml_answer_confidence",
    "ml_prediction_confidence",
    "current_best_prediction_confidence",
    "current_best_answer_confidence",
  ],
  [DetectorModes.MULTICHOICE]: [],
  [DetectorModes.TEXT_RECOGNITION]: ["iq_id", "created_at", "current_best_answer_str", "current_best_answer_source"],
  [DetectorModes.BOUNDING_BOX]: [],
};

function enhanceColumnWithDeleteModal(
  columns: GridColDef[],
  onOpenDeleteModal: (imagequery: ImageQuery) => void
): GridColDef[] {
  return columns.map((column) => {
    if (column.field === "image") {
      return {
        ...column,
        renderCell: (params: GridRenderCellParams<ImageQuery>) => {
          const data = params.row as ImageQuery;
          return <ImageQueryThumbnail detectorData={data} setIsDeleteModalOpen={() => onOpenDeleteModal(data)} />;
        },
      };
    }
    return column;
  });
}

export const renderWithTooltip = (value: string) => (
  // Just to render _sources fields adding a tooltip and elipsis when the text overflows
  <Typography>{value}</Typography>
);

export const getColumns = (detector?: Detector): GridColDef[] => {
  // Adding a suffix to each renderCell value when it's a COUNT detector
  let suffix = "";

  const mode = detector?.mode;
  if (detector && mode === DetectorModes.COUNT) {
    suffix = " Object";
  }

  // For columns ending in _str, we can add a suffix to the value
  const strRenderValue = (strValue: string | null) => {
    const answerValue = labelInternalToUI(labelMappingsIQ, strValue);
    const pluralizedSuffix =
      answerValue.toUpperCase() !== "UNCLEAR" ? pluralize(suffix, parseInt(answerValue, 10)) : "";
    return `${answerValue ? `${answerValue}${pluralizedSuffix}` : "--"}`;
  };

  return [
    {
      field: "image",
      headerName: "Thumbnail / Summary",
      headerAlign: "center",
      resizable: false,
      sortable: false,
      minWidth: 350,
      filterable: false,
      hideable: false,
      renderCell: (params: GridRenderCellParams<ImageQuery>) => {
        const data = params.row as ImageQuery;
        return <ImageQueryThumbnail detectorData={data} setIsDeleteModalOpen={() => {}} />;
      },
    },
    {
      field: "date",
      headerName: "Timestamp",
      headerAlign: "center",
      minWidth: 200,
      cellClassName: "justify-center cursor-pointer",
      filterable: false,
      renderCell: (params: GridRenderCellParams<ImageQuery>) => {
        const value = (params.row as ImageQuery).created_at;
        const valueStr = value?.toString();
        if (!valueStr) {
          return "";
        }

        return <Typography variant="detail">{convertUTCtoLocalReadableDate(valueStr)}</Typography>;
      },
    },
    {
      field: "current_best_answer_str",
      headerName: "Best Answer",
      headerAlign: "center",
      minWidth: 200,
      cellClassName: "justify-center cursor-pointer",
      renderHeader: () => (
        <Header
          title="Best Answer"
          description="The highest confidence answer (above confidence threshold) that Groundlight has for the query, regardless of when the answer arrived. If no prediction is above the confidence threshold, there is no best answer."
        />
      ),
      renderCell: (params: GridRenderCellParams<ImageQuery>) =>
        renderWithTooltip(strRenderValue((params.row as ImageQuery).current_best_answer_str)),
      sortable: true,
      filterable: false,
    },
    {
      field: "current_best_answer_source",
      headerName: "Best Answer Source",
      headerAlign: "center",
      minWidth: 220,
      cellClassName: "p-0 justify-center cursor-pointer ",
      renderCell: (params: GridRenderCellParams<ImageQuery>) =>
        renderWithTooltip(
          labelInternalToUI(sourceMappingsIQ, (params.row as ImageQuery).current_best_answer_source) || "None"
        ),
      sortable: true,
      filterable: false,
    },
    {
      field: "current_best_answer_confidence",
      headerName: "Best Answer Confidence",
      headerAlign: "center",
      minWidth: 200,
      cellClassName: "justify-center cursor-pointer",
      renderCell: (params: GridRenderCellParams<ImageQuery>) => {
        const confidence = (params.row as ImageQuery).current_best_answer_confidence;
        const confidenceStr = confidence !== null ? confidence.toFixed(4) : "--";
        return <Typography noWrap>{confidenceStr}</Typography>;
      },
      sortable: true,
      filterable: false,
    },
    {
      field: "best_answer_feedback_text",
      headerName: "Best Answer Feedback Text",
      headerAlign: "center",
      minWidth: 250,
      cellClassName: "justify-center cursor-pointer",
      renderHeader: () => (
        <Header
          title="Best Answer Feedback Text"
          description="The feedback text provided when the best answer was marked as unclear."
        />
      ),
      renderCell: (params: GridRenderCellParams<ImageQuery>) => {
        const feedbackText = (params.row as ImageQuery).latest_flagged_label_feedback_text;
        return <Typography noWrap>{feedbackText || "--"}</Typography>;
      },
      sortable: true,
      filterable: false,
    },
    {
      field: "system_answer_str",
      headerName: "First Answer", // (at First answer time)
      headerAlign: "center",
      minWidth: 200,
      cellClassName: "justify-center cursor-pointer",
      renderHeader: () => (
        <Header
          title="First Answer"
          description="The first answer (above confidence threshold) produced within first answer time. If Groundlight doesn't return a prediction above the confidence threshold in time, there is no first answer."
        />
      ),
      renderCell: (params: GridRenderCellParams<ImageQuery>) => (
        <Typography noWrap>{strRenderValue((params.row as ImageQuery).system_answer_str)}</Typography>
      ),
      filterable: false,
      description: "(at First answer time)",
    },
    {
      field: "system_answer_source",
      headerName: "First Answer Source", // (at First answer time)
      headerAlign: "center",
      minWidth: 200,
      cellClassName: "p-0 justify-center cursor-pointer",
      renderCell: (params: GridRenderCellParams<ImageQuery>) =>
        renderWithTooltip(
          labelInternalToUI(sourceMappingsIQ, (params.row as ImageQuery).system_answer_source) || "None"
        ),
      filterable: false,
      description: "(at First answer time)",
    },
    {
      field: "system_answer_confidence",
      headerName: "First Answer Confidence", // (at First answer time)
      headerAlign: "center",
      minWidth: 200,
      cellClassName: "justify-center cursor-pointer",
      renderCell: (params: GridRenderCellParams<ImageQuery>) => {
        const confidence = (params.row as ImageQuery).system_answer_confidence;
        const confidenceStr = confidence !== null ? confidence.toFixed(4) : "--";
        return <Typography noWrap>{confidenceStr}</Typography>;
      },
      sortable: true,
      filterable: false,
      description: "(at First answer time)",
    },
    {
      field: "first_answer_feedback_text",
      headerName: "First Answer Feedback Text",
      headerAlign: "center",
      minWidth: 250,
      cellClassName: "justify-center cursor-pointer",
      renderHeader: () => (
        <Header
          title="First Answer Feedback Text"
          description="The feedback text provided when the first answer was marked as unclear."
        />
      ),
      renderCell: (params: GridRenderCellParams<ImageQuery>) => {
        const feedbackText = (params.row as ImageQuery).latest_flagged_label_feedback_text;
        return <Typography noWrap>{feedbackText || "--"}</Typography>;
      },
      sortable: true,
      filterable: false,
    },
    {
      field: "ml_answer_str",
      headerName: "First ML",
      headerAlign: "center",
      minWidth: 200,
      cellClassName: "justify-center cursor-pointer",
      renderHeader: () => (
        <Header
          title="First ML"
          description="The first ML prediction for the query that Groundlight produced within first answer time."
        />
      ),
      renderCell: (params: GridRenderCellParams<ImageQuery>) => (
        <Typography noWrap>{strRenderValue((params.row as ImageQuery).ml_prediction_str)}</Typography>
      ),
      sortable: true,
      filterable: false,
    },
    {
      field: "ml_answer_confidence",
      headerName: "First ML Confidence",
      headerAlign: "center",
      minWidth: 200,
      cellClassName: "justify-center cursor-pointer",
      renderCell: (params: GridRenderCellParams<ImageQuery>) => {
        const confidence = (params.row as ImageQuery).ml_prediction_confidence;
        const confidenceStr = confidence !== null ? confidence.toFixed(4) : "--";
        return <Typography noWrap>{confidenceStr}</Typography>;
      },
      sortable: true,
      filterable: false,
    },
    {
      field: "iq_id",
      headerName: "Query ID",
      headerAlign: "center",
      minWidth: 250,
      cellClassName: "justify-center cursor-pointer",
      filterable: true,
      renderCell: (params: GridRenderCellParams<ImageQuery>) => (
        <Typography variant="small" noWrap>
          {params.row.iq_id}
        </Typography>
      ),
    },
    {
      field: "current_best_prediction_str",
      headerName: "Best Prediction",
      headerAlign: "center",
      minWidth: 200,
      cellClassName: "justify-center cursor-pointer",
      renderHeader: () => (
        <Header
          title="Best Prediction"
          description="The highest confidence prediction (regardless of confidence threshold) that groundlight has for the query (regardless of source or when the prediction arrived)."
        />
      ),
      renderCell: (params: GridRenderCellParams<ImageQuery>) => (
        <Typography noWrap>{strRenderValue((params.row as ImageQuery).current_best_prediction_str)}</Typography>
      ),
      sortable: true,
      filterable: false,
    },
    {
      field: "current_best_prediction_source",
      headerName: "Best Prediction Source",
      headerAlign: "center",
      minWidth: 200,
      cellClassName: "p-0 justify-center cursor-pointer",
      renderCell: (params: GridRenderCellParams<ImageQuery>) =>
        renderWithTooltip(
          labelInternalToUI(sourceMappingsIQ, (params.row as ImageQuery).current_best_prediction_source) || "None"
        ),
      sortable: true,
      filterable: false,
    },
    {
      field: "current_best_prediction_confidence",
      headerName: "Best Prediction Confidence",
      headerAlign: "center",
      minWidth: 200,
      cellClassName: "justify-center cursor-pointer",
      renderCell: (params: GridRenderCellParams<ImageQuery>) => {
        const confidence = (params.row as ImageQuery).current_best_prediction_confidence;
        const confidenceStr = confidence !== null ? confidence.toFixed(4) : "--";
        return <Typography noWrap>{confidenceStr}</Typography>;
      },
      sortable: true,
      filterable: false,
    },
    {
      field: "system_prediction_str",
      headerName: "Best in First Answer Time",
      headerAlign: "center",
      minWidth: 250,
      cellClassName: "justify-center cursor-pointer",
      renderHeader: () => (
        <Header
          title="Best in First Answer Time"
          description="The highest confidence answer or prediction that Groundlight has at the end of first answer time (regardless of source or confidence threshold)."
        />
      ),
      renderCell: (params: GridRenderCellParams<ImageQuery>) => (
        <Typography noWrap>{strRenderValue((params.row as ImageQuery).system_prediction_str)}</Typography>
      ),
      sortable: true,
      filterable: false,
    },
    {
      field: "system_prediction_source",
      headerName: "Best in First Answer Time Source",
      headerAlign: "center",
      minWidth: 250,
      cellClassName: "p-0 justify-center cursor-pointer",
      renderCell: (params: GridRenderCellParams<ImageQuery>) =>
        renderWithTooltip(
          labelInternalToUI(sourceMappingsIQ, (params.row as ImageQuery).system_prediction_source) || "None"
        ),
      sortable: true,
      filterable: false,
    },
    {
      field: "system_prediction_confidence",
      headerName: "Best in First Answer Time Confidence",
      headerAlign: "center",
      minWidth: 250,
      cellClassName: "justify-center cursor-pointer",
      renderCell: (params: GridRenderCellParams<ImageQuery>) => {
        const confidence = (params.row as ImageQuery).system_prediction_confidence;
        const confidenceStr = confidence !== null ? confidence.toFixed(4) : "--";
        return <Typography noWrap>{confidenceStr}</Typography>;
      },
      sortable: true,
      filterable: false,
    },
    {
      field: "ml_prediction_str",
      headerName: "Best ML in First Answer Time",
      headerAlign: "center",
      minWidth: 250,
      cellClassName: "justify-center cursor-pointer",
      renderHeader: () => (
        <Header
          title="Best ML in First Answer Time"
          description="The best ML prediction that Groundlight has (regardless of confidence threshold) at the end of first answer time."
        />
      ),
      renderCell: (params: GridRenderCellParams<ImageQuery>) => (
        <Typography noWrap>{strRenderValue((params.row as ImageQuery).ml_prediction_str)}</Typography>
      ),
      sortable: true,
      filterable: false,
    },
    {
      field: "ml_prediction_confidence",
      headerName: "Best ML in First Answer Time Confidence",
      headerAlign: "center",
      minWidth: 250,
      cellClassName: "justify-center cursor-pointer",
      renderCell: (params: GridRenderCellParams<ImageQuery>) => {
        const confidence = (params.row as ImageQuery).ml_prediction_confidence;
        const confidenceStr = confidence !== null ? confidence.toFixed(4) : "--";
        return <Typography noWrap>{confidenceStr}</Typography>;
      },
      sortable: true,
      filterable: false,
    },
    {
      field: "is_ground_truth",
      headerName: "Is Ground Truth?",
      headerAlign: "center",
      minWidth: 200,
      cellClassName: "justify-center cursor-pointer",
      renderCell: (params: GridRenderCellParams<ImageQuery>) => {
        const label = (params.row as ImageQuery).ground_truth_str ? "True" : "False";
        return (
          <Typography className="text-base" noWrap>
            {label}
          </Typography>
        );
      },
      sortable: true,
      filterable: false,
    },
    {
      field: "is_audited",
      headerName: "Audited (GL)",
      headerAlign: "center",
      minWidth: 200,
      cellClassName: "justify-center cursor-pointer",
      renderCell: (params: GridRenderCellParams<ImageQuery>) => {
        const label = (params.row as ImageQuery).is_audited ? "True" : "False";
        return <Typography className="text-base">{label}</Typography>;
      },
      sortable: true,
      filterable: false,
    },
    // {
    //   field: "ground_truth_str",
    //   headerName: "Ground Truth Answer",
    //   headerAlign: "center",
    //   minWidth: 240,
    //   cellClassName: "justify-center cursor-pointer",
    //   renderCell: (params) => {
    //     const label = (params.row.gro as ImageQuery)und_truth_str
    //       ? labelInternalToUI(labelMappingsIQ, (params.row as ImageQuery).ground_truth_str)
    //       : "Missing";

    //     return (
    //       <Typography className={`${label === "Missing" ? "text-red-700" : "text-black"} text-base`} noWrap={false}>
    //         {label}
    //       </Typography>
    //     );
    //   },
    //   sortable: true,
    //   filterable: false,
    // },
    // {
    //   field: "ground_truth_source",
    //   headerName: "Ground Truth Source",
    //   headerAlign: "center",
    //   minWidth: 160,
    //   cellClassName: "justify-center cursor-pointer",
    //   renderCell: (params) => {
    //     const label = (params.row.gro as ImageQuery)und_truth_source
    //       ? labelInternalToUI(sourceMappingsIQ, (params.row as ImageQuery).ground_truth_source)
    //       : "Missing";

    //     return (
    //       <Typography className={`${label === "Missing" ? "text-red-700" : "text-black"} text-base`} noWrap={false}>
    //         {label}
    //       </Typography>
    //     );
    //   },
    //   sortable: true,
    //   filterable: false,
    // },
    // {
    //   field: "ground_truth_confidence",
    //   headerName: "Ground Truth Confidence",
    //   headerAlign: "center",
    //   minWidth: 240,
    //   cellClassName: "justify-center cursor-pointer",
    //   renderCell: (params) => {
    //     const label = (params.row.gro as ImageQuery)und_truth_confidence
    // ? (params.row.gro as ImageQuery)und_truth_confidence.toFixed(4) : "Missing";

    //     return (
    //       <Typography className={`${label === "Missing" ? "text-red-700" : "text-black"} text-base`} noWrap={false}>
    //         {label}
    //       </Typography>
    //     );
    //   },
    //   sortable: true,
    //   filterable: false,
    // },
    // {
    //   field: "in_queue",
    //   headerName: "In Review Queue?",
    //   headerAlign: "center",
    //   minWidth: 160,
    //   cellClassName: "justify-center cursor-pointer",
    //   filterable: false,
    // },
    // {
    //   field: "patience_time",
    //   headerName: "First answer time",
    //   headerAlign: "center",
    //   minWidth: 160,
    //   cellClassName: "justify-center cursor-pointer",
    //   filterable: false,
    // },
    // TODO: Not yet implemented
    // {
    //   field: "ml_answer_confidence",
    //   headerName: "<Annotation Type> 1 (String)",
    // },
    // {
    //   field: "ml_answer_confidence",
    //   headerName: "Feedback",
    // },
  ].map((column) => {
    if (column.hideable !== undefined || mode === undefined || HIDEABLE_COLUMNS_BY_MODE[mode].length === 0) {
      return column;
    }

    return {
      ...column,
      hideable: HIDEABLE_COLUMNS_BY_MODE[mode].includes(column.field as never),
    };
  }) as GridColDef[];
};

export { enhanceColumnWithDeleteModal };
